import { HTMLContainer, useEditor, useIsEditing } from "./canvas";
import { useValue } from "@tldraw/state-react";
import { FileHelpers } from "@tldraw/utils";
import type { TLVideoShape } from "@tldraw/tlschema";
import { useEffect, useRef, useState } from "react";
import { notify, uploadFile } from "./api";
import "./video-controls.css";

export function BoardVideo({ shape }: { shape: TLVideoShape }) {
  const editor = useEditor(),
    editing = useIsEditing(shape.id);
  const selected = useValue(
    "video selected",
    () => editor.getSelectedShapeIds().includes(shape.id),
    [editor, shape.id],
  );
  const video = useRef<HTMLVideoElement>(null);
  const posterJob = useRef<AbortController | null>(null);
  const [savingPoster, setSavingPoster] = useState(false);
  useEffect(() => () => posterJob.current?.abort(), []);
  const visible = useValue(
    "video visibility",
    () =>
      editor
        .getShapePageBounds(shape.id)
        ?.collides(editor.getViewportPageBounds()) ?? false,
    [editor, shape.id],
  );
  const asset = useValue(
    "video asset",
    () =>
      shape.props.assetId ? editor.getAsset(shape.props.assetId) : undefined,
    [editor, shape.props.assetId],
  );
  if (!visible) return null;
  const poster =
    typeof shape.meta.poster === "string" ? shape.meta.poster : undefined;
  const src = typeof asset?.props.src === "string" ? asset.props.src : "";
  const file = /^\/api\/rooms\/[\w-]+\/files\/[\w-]+$/.test(src);
  const choosePoster = async () => {
    const player = video.current;
    const room = /^\/api\/rooms\/([\w-]+)\/files\/[\w-]+$/.exec(src)?.[1];
    if (
      !player ||
      !room ||
      !player.videoWidth ||
      !player.videoHeight ||
      editor.getIsReadonly() ||
      shape.isLocked
    )
      return;
    const controller = new AbortController();
    posterJob.current = controller;
    setSavingPoster(true);
    const scale = Math.min(
      1,
      2048 / Math.max(player.videoWidth, player.videoHeight),
      Math.sqrt(4_000_000 / (player.videoWidth * player.videoHeight)),
    );
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(player.videoWidth * scale));
    canvas.height = Math.max(1, Math.round(player.videoHeight * scale));
    try {
      const ctx = canvas.getContext("2d");
      if (!ctx) throw new Error("Could not capture this video frame.");
      ctx.drawImage(player, 0, 0, canvas.width, canvas.height);
      const blob = await new Promise<Blob>((resolve, reject) =>
        canvas.toBlob(
          (value) =>
            value
              ? resolve(value)
              : reject(new Error("Could not save this frame.")),
          "image/jpeg",
          0.86,
        ),
      );
      controller.signal.throwIfAborted();
      const uploaded = await uploadFile(
        room,
        new File([blob], "Video poster.jpg", { type: "image/jpeg" }),
        controller.signal,
      );
      controller.signal.throwIfAborted();
      const current = editor.getShape<TLVideoShape>(shape.id);
      if (
        !current ||
        current.isLocked ||
        editor.getIsReadonly() ||
        current.props.assetId !== shape.props.assetId
      )
        throw new Error("The video changed while saving its poster.");
      editor.markHistoryStoppingPoint("Choose video poster");
      editor.updateShape({
        id: shape.id,
        type: "video",
        meta: { ...current.meta, poster: uploaded.src },
      });
      notify("Poster frame saved for the board and PDF export.");
    } catch (error) {
      if (!controller.signal.aborted) notify((error as Error).message);
    } finally {
      canvas.width = 0;
      canvas.height = 0;
      posterJob.current = null;
      setSavingPoster(false);
    }
  };
  return (
    <HTMLContainer
      style={{
        width: shape.props.w,
        height: shape.props.h,
        overflow: "hidden",
        background: "#eee",
      }}
    >
      {editing && asset?.props.src ? (
        <>
          <video
            ref={video}
            controls
            playsInline
            preload="metadata"
            autoPlay
            poster={poster}
            src={asset.props.src}
            aria-label={shape.props.altText || "Concept video"}
            style={{ width: "100%", height: "100%", pointerEvents: "all" }}
            onPointerDown={(event) => event.stopPropagation()}
          />
          <div
            className="board-video-actions"
            onPointerDown={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              disabled={
                savingPoster ||
                !file ||
                editor.getIsReadonly() ||
                shape.isLocked
              }
              onClick={() => void choosePoster()}
            >
              {savingPoster ? "Saving poster…" : "Use this frame as poster"}
            </button>
            <button type="button" onClick={() => editor.setEditingShape(null)}>
              Done
            </button>
          </div>
        </>
      ) : (
        <>
          <img
            src={poster}
            alt="Video poster"
            style={{ width: "100%", height: "100%", objectFit: "contain" }}
          />
          <span
            style={{
              position: "absolute",
              bottom: 8,
              left: 8,
              background: "#fff",
              padding: "5px 9px",
              borderRadius: 5,
              fontSize: 12,
            }}
          >
            ▶ Video · double-click to play
          </span>
          {selected && (
            <div
              className="board-video-actions"
              onPointerDown={(event) => event.stopPropagation()}
            >
              <button
                type="button"
                onClick={() => editor.setEditingShape(shape.id)}
              >
                Play
              </button>
              {file && (
                <a
                  href={src}
                  download={
                    asset?.type === "video" ? asset.props.name : "Video.mp4"
                  }
                >
                  Download
                </a>
              )}
            </div>
          )}
        </>
      )}
    </HTMLContainer>
  );
}
/** A video exports as its chosen poster frame, labelled; without one it is left out. */
export async function videoPosterSvg(shape: TLVideoShape) {
  const poster = shape.meta.poster;
  if (
    typeof poster !== "string" ||
    !/^\/api\/rooms\/[\w-]+\/files\/[\w-]+$/.test(poster)
  )
    return null;
  const response = await fetch(poster);
  if (!response.ok) throw new Error("Video poster unavailable for export.");
  const data = await FileHelpers.blobToDataUrl(await response.blob());
  return (
    <g>
      <image href={data} width={shape.props.w} height={shape.props.h} />
      <rect
        x={0}
        y={shape.props.h - 24}
        width={65}
        height={24}
        fill="white"
      />
      <text x={6} y={shape.props.h - 7} fontSize={12} fill="#28372e">
        Video
      </text>
    </g>
  );
}
